import React, { useState, useEffect, useCallback, useRef } from "react";
import CytoscapeComponent from "react-cytoscapejs";
import cytoscape from "cytoscape";
import cycola from "cytoscape-cola";
import { IoResize } from "react-icons/io5";
import { FiPlus, FiMinus } from "react-icons/fi";
import SchemaInfo from "../common/SchemaInfo";
import nodeService from "../../services/nodeService";
import { useUserId } from "../../Utils/helpers";
import { useSchemaGraph } from "../../hooks/useSchemaGraph";

cytoscape.use(cycola);

const TopicGraph = ({ data, cyRef, onCyRef, schemaRef }) => {
  const [elements, setElements] = useState([]);
  const [cy, setCy] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSchemaData, setSelectedSchemaData] = useState(null);
  const [fetchedData, setFetchedData] = useState(null);
  const userId = useUserId();
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const zoomInInterval = useRef(null);
  const zoomOutInterval = useRef(null);
  const { cy: cyschema, loading, error } = useSchemaGraph();

  useEffect(() => {
    if (data) {
      const { nodes, edges } = data;
      setNodes(nodes);
      setEdges(edges);
      const filteredStructure = FilteredTopicStructure(nodes, edges);
      const cytoscapeElements = convertToCytoscapeElements(
        filteredStructure.nodes,
        filteredStructure.edges,
        filteredStructure.nodeMap
      );
      setElements(cytoscapeElements);
    }
  }, [data]);

  const FilteredTopicStructure = (nodes, edges) => {
    const nodeMap = new Map();
    const usedValues = new Set();

    nodes.forEach((node) => {
      if (node.class === "Topic" && node.props.name) {
        let baseValue = `${node.props.name.toLowerCase().replace(/\s+/g, "")}_${
          node.id
        }`;
        let uniqueValue = baseValue;

        let counter = 1;
        while (usedValues.has(uniqueValue)) {
          uniqueValue = `${baseValue}_${counter}`;
          counter++;
        }

        nodeMap.set(node.id, {
          ...node,
          value: `${node.props.name.toLowerCase().replace(/\s+/g, "")}_${
            node.id
          }`,
          label: node.props.name,
          children: [],
        });

        usedValues.add(uniqueValue);
      }
    });

    const edgesBySource = new Map();
    edges.forEach((edge) => {
      if (!edgesBySource.has(edge.source)) {
        edgesBySource.set(edge.source, []);
      }
      edgesBySource.get(edge.source).push(edge.target);
    });

    const buildTree = (nodeId) => {
      const node = nodeMap.get(nodeId);
      if (!node) return null;

      const newNode = { ...node, children: [] };

      const childIds = edgesBySource.get(nodeId) || [];
      childIds.forEach((childId) => {
        const childNode = buildTree(childId);
        if (childNode) {
          newNode.children.push(childNode);
        }
      });

      return newNode;
    };

    const rootNodes = Array.from(nodeMap.keys()).filter(
      (id) => nodeMap.get(id).label === "All Topics"
    );
    return { nodes: Array.from(nodeMap.values()), edges, nodeMap };
  };

  const convertToCytoscapeElements = (nodes, edges, nodeMap) => {
    const cytoscapeNodes = nodes.map((n) => ({
      data: {
        id: n.id.toString(),
        label: n.name,
        colour: n.colour,
        ...n,
      },
      classes: n.labels ? n.labels.join(" ") : "",
    }));

    const nodeIds = new Set(cytoscapeNodes.map((node) => node.data.id));

    const cytoscapeEdges = edges
      .filter((edge) => {
        return (
          nodeIds.has(nodeMap.get(edge.source)?.id) &&
          nodeIds.has(nodeMap.get(edge.target)?.id)
        );
      })
      .map((edge) => ({
        data: {
          id: edge.id,
          source: nodeMap.get(edge.source).id,
          target: nodeMap.get(edge.target).id,
          label: "is parent of",
        },
      }));

    return [...cytoscapeNodes, ...cytoscapeEdges];
  };

  const layout = {
    name: "dagre",
    nodeDimensionsIncludeLabels: true,
    rankDir: "LR", //BT',
    padding: 10,
    ranker: "longest-path",
    spacingFactor: 1.0,
    spacingFactor: 1.5,
    avoidOverlap: true,
    nodeDimensionsIncludeLabels: true,
    roots: elements
      .filter((el) => el.data.label === "All Topics")
      .map((el) => el.data.id),
    maximal: false,
    circle: false,
  };

  // const layout = {
  //   name: "breadthfirst",
  //   directed: true,
  //   padding: 10,
  //   spacingFactor: 1.5,
  //   avoidOverlap: true,
  //   nodeDimensionsIncludeLabels: true,
  //   roots: elements
  //     .filter((el) => el.data.label === "All Topics")
  //     .map((el) => el.data.id),
  //   grid: true,
  //   fit: true,
  //   animate: true,
  //   animationDuration: 500,
  //   maximal: false,
  //   circle: false,
  //   transform: (node, position) => ({
  //     x: position.y,
  //     y: position.x,
  //   }),
  // };

  const stylesheet = [
    {
      selector: "node",
      style: {
        content: "data(label)",
        "border-width": "0px",
        // "border-color": "none",
        "text-valign": "bottom",
        color: "black",
        "text-outline-width": 2,
        "background-color": "#00FF00",
        "text-outline-color": "#FFF",
        "font-size": "12px",
        "background-blacken": 0,
        "text-wrap": "wrap",
        "text-max-width": "100px",
      },
    },
    {
      selector: "edge",
      style: {
        content: "data(label)",
        "curve-style": "bezier",
        "target-arrow-shape": "triangle",
        "target-arrow-color": "#ccc",
        "line-color": "#ccc",
        width: 1.5,
        "font-size": "10px",
        "edge-text-rotation": "autorotate",
        "text-opacity": 1.0,
        opacity: 0.9,
      },
    },
  ];

  const handleSchemaClick = (schemaData) => {
    setSelectedSchemaData(schemaData);
    setIsModalOpen(true);
  };

  const getAllNodes = useCallback(
    async (nodeId) => {
      const clickedNode = data.nodes.find(
        (node) => node.id === parseInt(nodeId)
      );
      const nodeOId = clickedNode ? clickedNode.props._oid : null;

      if (!nodeOId) {
        console.error("Node ID (_oid) is not found in the element data.");
        return;
      }

      try {
        const childNodesResponse = await nodeService.fetchChildNodes(
          nodeOId,
          userId
        );

        // Filter edges to remove self-loops (edges where source and target are the same)
        const filteredEdges = childNodesResponse?.edges.filter(
          (edge) => edge.source !== edge.target
        );
        childNodesResponse.edges = filteredEdges;

        setFetchedData({
          nodes: childNodesResponse.nodes,
          edges: childNodesResponse.edges,
        });
      } catch (error) {
        console.error("Failed to fetch child nodes:", error);
      }
    },
    [data.nodes, userId]
  );

  useEffect(() => {
    if (cy) {
      const fetchNodeRelations = (nodeId) => {
        return edges
          .map((edge) => {
            const relationId = edge.id;
            let relationType = "";
            let relatedNodeName = "";

            const getRelatedNode = (id) => {
              const node =
                fetchedData?.nodes.find((n) => n.id === id) ||
                nodes.find((n) => n.id === id);
              return node;
            };

            if (edge.source === parseInt(nodeId)) {
              relationType = "to";
              const relatedNode = getRelatedNode(edge.target);
              relatedNodeName = relatedNode
                ? relatedNode.props.name
                : "Unknown";
            } else if (edge.target === parseInt(nodeId)) {
              relationType = "from";
              const relatedNode = getRelatedNode(edge.source);
              relatedNodeName = relatedNode
                ? relatedNode.props.name
                : "Unknown";
            }

            return relationType
              ? {
                  relationId,
                  className: edge.class,
                  relationType,
                  relatedNodeName,
                }
              : null;
          })
          .filter(Boolean); // Remove null values
      };

      const handleNodeClick = (event) => {
        const nodeId = event.target.id();
        getAllNodes(nodeId);

        let clickedNode =
          fetchedData?.nodes.find((node) => node.id === parseInt(nodeId)) ||
          nodes.find((node) => node.id === parseInt(nodeId));

        if (clickedNode) {
          const nodeRelations = fetchNodeRelations(nodeId);
          const updatedNode = { ...clickedNode, relations: nodeRelations };
          handleSchemaClick(updatedNode);
        } else {
          console.warn(`Node with ID ${nodeId} not found.`);
        }
      };

      cy.on("tap", "node", handleNodeClick);
    }
  }, [cy, edges, fetchedData, getAllNodes, nodes]);

  const throttle = (func, limit) => {
    let lastFunc;
    let lastRan;
    return function (...args) {
      if (!lastRan) {
        func.apply(this, args);
        lastRan = Date.now();
      } else {
        clearTimeout(lastFunc);
        lastFunc = setTimeout(() => {
          if (Date.now() - lastRan >= limit) {
            func.apply(this, args);
            lastRan = Date.now();
          }
        }, limit - (Date.now() - lastRan));
      }
    };
  };

  const zoomIn = throttle(() => {
    if (cy) {
      const newZoom = cy.zoom() * 1.2;
      const center = {
        x: cy.container().clientWidth / 2,
        y: cy.container().clientHeight / 2,
      };
      cyRef.zoom({
        level: newZoom,
        renderedPosition: center,
      });
    }
  }, 100);

  const zoomOut = throttle(() => {
    if (cy) {
      const newZoom = cy.zoom() / 1.2;
      const center = {
        x: cy.container().clientWidth / 2,
        y: cy.container().clientHeight / 2,
      };
      cyRef.zoom({
        level: newZoom,
        renderedPosition: center,
      });
    }
  }, 100);

  const resetGraph = () => {
    if (cy) {
      cy.fit();
    }
  };

  const closeSchemaModal = () => {
    setIsModalOpen(false);
  };

  const startContinuousZoomIn = () => {
    if (!zoomInInterval.current) {
      zoomInInterval.current = setInterval(zoomIn, 100); // Adjust interval speed if needed
    }
  };

  const startContinuousZoomOut = () => {
    if (!zoomOutInterval.current) {
      zoomOutInterval.current = setInterval(zoomOut, 100);
    }
  };

  const stopContinuousZoom = () => {
    if (zoomInInterval.current) {
      clearInterval(zoomInInterval.current);
      zoomInInterval.current = null;
    }
    if (zoomOutInterval.current) {
      clearInterval(zoomOutInterval.current);
      zoomOutInterval.current = null;
    }
  };

  return (
    <div style={{ position: "relative" }}>
      <div className="zoom-controls">
        <div className="zoom-box" onClick={resetGraph}>
          <IoResize className="zoom-icon-reset" />
        </div>
        <div
          className="zoom-box"
          onMouseDown={startContinuousZoomIn}
          onMouseUp={stopContinuousZoom}
          onMouseLeave={stopContinuousZoom}
          onClick={zoomIn}
        >
          <FiPlus className="zoom-icon" />
        </div>
        <div
          className="zoom-box"
          onMouseDown={startContinuousZoomOut}
          onMouseUp={stopContinuousZoom}
          onMouseLeave={stopContinuousZoom}
          onClick={zoomOut}
        >
          <FiMinus className="zoom-icon" />
        </div>
      </div>
      <CytoscapeComponent
        cy={(cy) => {
          setCy(cy);
          onCyRef(cy);
        }}
        elements={elements}
        layout={layout}
        stylesheet={stylesheet}
        style={{ width: "100%", height: window.innerHeight - 120 }}
        minZoom={0.1}
        maxZoom={3}
      />
      {isModalOpen && (
        <SchemaInfo
          show={isModalOpen}
          handleClose={closeSchemaModal}
          data={selectedSchemaData}
          cy={cyschema}
          isTopic={true}
        />
      )}
    </div>
  );
};

export default TopicGraph;
